import React, { useState, useMemo, useRef } from "react";
import "../../App.css";
import { useApp } from '../../core/context';
import { uuid } from '../../core/helpers';
import {
  ORG_JSON_KEYS, persistOrgJsonSetting, DEFAULT_FREE_RESOURCES,
  clientPurchasedBundleIds, isFreeResourceUnlocked,
} from '../../core/org-json-settings';
import { Icon, InlineIcon } from '../../components/Icon';
import { uploadClientDoc, publicDocUrl } from '../../storage';
import { downloadDocument } from '../../mobile/documentActions';

function giftDocRef(gift) {
  const url = String(gift.downloadUrl || '').trim();
  const name = `${gift.name}.${(gift.format || 'pdf').toLowerCase()}`;
  if (url && url !== '#') {
    if (gift.storagePath) return { path: gift.storagePath, name: gift.fileName || name };
    if (!url.includes('://') && !url.startsWith('/')) return { path: url, name: gift.fileName || name };
    return { url, name: gift.fileName || name };
  }
  if (gift.storagePath) return { path: gift.storagePath, name: gift.fileName || name };
  return null;
}

const EMPTY_DRAFT = {
  name: "", desc: "", icon: "gift", format: "PDF", pages: 8,
  triggerOn: "registration_complete", downloadUrl: "", bundleIds: [], fileName: "",
};

function giftToDraft(gift) {
  return {
    name: gift.name || "",
    desc: gift.desc || "",
    icon: gift.icon || "gift",
    format: gift.format || "PDF",
    pages: Number(gift.pages) || 1,
    triggerOn: gift.triggerOn || "registration_complete",
    downloadUrl: gift.downloadUrl && gift.downloadUrl !== "#" ? gift.downloadUrl : "",
    bundleIds: gift.bundleIds || [],
    fileName: gift.fileName || "",
  };
}

function GiftFormFields({ draft, setDraft, bundles, toggleBundle, fileRef, uploading, existingFileName }) {
  return (
    <div className="form-grid">
      <div className="f-group">
        <label className="f-label">Name</label>
        <input className="f-input" value={draft.name} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))} />
      </div>
      <div className="f-group">
        <label className="f-label">Description</label>
        <textarea className="f-textarea" rows={3} value={draft.desc} onChange={e => setDraft(d => ({ ...d, desc: e.target.value }))} placeholder="Short summary shown on the card" />
      </div>
      <div className="form-grid-2">
        <div className="f-group">
          <label className="f-label">Pages</label>
          <input className="f-input" type="number" min={1} value={draft.pages} onChange={e => setDraft(d => ({ ...d, pages: e.target.value }))} />
        </div>
        <div className="f-group">
          <label className="f-label">Format</label>
          <input className="f-input" value={draft.format} onChange={e => setDraft(d => ({ ...d, format: e.target.value }))} placeholder="PDF" />
        </div>
      </div>
      <div className="f-group">
        <label className="f-label">{existingFileName ? "Replace document (optional)" : "Upload document"}</label>
        <input ref={fileRef} type="file" className="f-input" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip" onChange={e => setDraft(d => ({ ...d, fileName: e.target.files?.[0]?.name || "" }))} />
        {existingFileName && !draft.fileName && <div className="f-hint">Current file: {existingFileName}</div>}
        {draft.fileName && <div className="f-hint">New file: {draft.fileName}</div>}
      </div>
      <div className="f-group">
        <label className="f-label">Or external download URL</label>
        <input className="f-input" value={draft.downloadUrl} onChange={e => setDraft(d => ({ ...d, downloadUrl: e.target.value }))} placeholder="https://… (optional if file uploaded)" />
      </div>
      <div className="f-group">
        <label className="f-label">Unlock when</label>
        <select className="f-select" value={draft.triggerOn} onChange={e => setDraft(d => ({ ...d, triggerOn: e.target.value }))}>
          <option value="always">Always available</option>
          <option value="registration_complete">Registration tasks complete</option>
          <option value="bundle_purchase">Linked bundle purchased</option>
        </select>
      </div>
      {(draft.triggerOn === "bundle_purchase" || draft.bundleIds.length > 0) && (
        <div className="f-group" style={{ gridColumn: "1/-1" }}>
          <label className="f-label">Linked bundles</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {(bundles || []).map(b => (
              <label key={b.id} className="f-check" style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                <input type="checkbox" checked={draft.bundleIds.includes(b.id)} onChange={() => toggleBundle(b.id)} />
                {b.name}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function FreeGiftsPage() {
  const { tasks, invoices, bundles, user, visibleCompanyIds, org, setOrg, showToast } = useApp();
  const isAdmin = user?.role === "admin";
  const isStaff = isAdmin || user?.role === "manager";
  const gifts = useMemo(
    () => (Array.isArray(org?.freeResources) && org.freeResources.length ? org.freeResources : DEFAULT_FREE_RESOURCES),
    [org?.freeResources]
  );

  const myClientIds = visibleCompanyIds || [];
  const myTasks = tasks.filter(t => myClientIds.includes(t.clientId));
  const totalTasks = myTasks.length;
  const doneTasks = myTasks.filter(t => t.status === "completed").length;
  const regComplete = totalTasks > 0 && doneTasks === totalTasks;
  const progress = totalTasks > 0 ? Math.round(doneTasks / totalTasks * 100) : 0;

  const purchasedBundleIds = useMemo(() => {
    const all = new Set();
    for (const cid of myClientIds) {
      clientPurchasedBundleIds(cid, invoices, bundles).forEach(id => all.add(id));
    }
    return all;
  }, [myClientIds, invoices, bundles]);

  const unlocked = (gift) => isFreeResourceUnlocked(gift, { regComplete, purchasedBundleIds, bundles });

  const isOpenForUser = (gift) => {
    if (gift.locked) return false;
    if (isStaff) return true;
    return unlocked(gift);
  };

  const hasFile = (gift) => !!giftDocRef(gift);

  const promptAttach = (giftId) => {
    pendingAttachRef.current = giftId;
    setAttachGiftId(giftId);
    attachRef.current?.click();
  };

  const download = async (gift) => {
    const ref = giftDocRef(gift);
    if (!ref) {
      if (isStaff) {
        showToast(`No file on "${gift.name}" yet — pick a file to attach.`, "info");
        promptAttach(gift.id);
        return;
      }
      showToast(`"${gift.name}" download link not configured yet — contact your team.`, "info");
      return;
    }
    try {
      await downloadDocument(ref, ref.name);
    } catch (e) {
      showToast(e.message || "Download failed", "error");
    }
  };

  const toggleGiftLock = async (giftId) => {
    const next = gifts.map(g => (g.id === giftId ? { ...g, locked: !g.locked } : g));
    try {
      await persistOrgJsonSetting(ORG_JSON_KEYS.freeResources, next, setOrg, 'freeResources');
      const g = gifts.find(x => x.id === giftId);
      showToast(g?.locked ? `"${g?.name}" unlocked for clients` : `"${g?.name}" locked for clients`, "success");
    } catch {
      showToast("Could not update lock state", "error");
    }
  };

  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [attachGiftId, setAttachGiftId] = useState(null);
  const fileRef = useRef(null);
  const attachRef = useRef(null);
  const pendingAttachRef = useRef(null);
  const [draft, setDraft] = useState({ ...EMPTY_DRAFT });

  const editingGift = editId ? gifts.find(g => g.id === editId) : null;
  const showForm = showAdd || !!editId;

  const openAdd = () => {
    setEditId(null);
    setDraft({ ...EMPTY_DRAFT });
    if (fileRef.current) fileRef.current.value = "";
    setShowAdd(true);
  };

  const openEdit = (gift) => {
    setShowAdd(false);
    setEditId(gift.id);
    setDraft(giftToDraft(gift));
    if (fileRef.current) fileRef.current.value = "";
  };

  const closeForm = () => {
    setShowAdd(false);
    setEditId(null);
    setDraft({ ...EMPTY_DRAFT });
    if (fileRef.current) fileRef.current.value = "";
  };

  const uploadDraftFile = async (file) => {
    if (!file) return null;
    const safeName = `${Date.now()}_${file.name.replace(/[^\w.-]+/g, "_")}`;
    const blobPath = `org/free-resources/${safeName}`;
    setUploading(true);
    try {
      await uploadClientDoc(blobPath, file);
      return {
        downloadUrl: publicDocUrl(blobPath, file.name),
        storagePath: blobPath,
        fileName: file.name,
      };
    } finally {
      setUploading(false);
    }
  };

  const saveGift = async () => {
    if (!draft.name.trim()) { showToast("Name required", "error"); return; }
    const existing = editingGift;
    let downloadUrl = draft.downloadUrl.trim() || existing?.downloadUrl || "";
    let storagePath = existing?.storagePath;
    let fileName = existing?.fileName;
    const file = fileRef.current?.files?.[0];
    if (file) {
      try {
        const uploaded = await uploadDraftFile(file);
        downloadUrl = uploaded.downloadUrl;
        storagePath = uploaded.storagePath;
        fileName = uploaded.fileName;
      } catch (e) {
        showToast(e.message || "Upload failed", "error");
        return;
      }
    }
    if (!downloadUrl && !storagePath && !editId) {
      showToast("Upload a document or paste a download URL", "error");
      return;
    }
    const item = {
      ...(existing || {}),
      id: existing?.id || ("g_" + uuid().slice(0, 8)),
      name: draft.name.trim(),
      desc: draft.desc.trim(),
      icon: draft.icon || existing?.icon || "gift",
      format: draft.format || (file?.name?.split(".").pop()?.toUpperCase() || existing?.format || "PDF"),
      pages: Math.max(1, Number(draft.pages) || 1),
      triggerOn: draft.triggerOn,
      downloadUrl,
      storagePath: storagePath || undefined,
      fileName: fileName || undefined,
      color: existing?.color || "var(--gold)",
      bundleIds: draft.bundleIds || [],
      locked: existing?.locked || false,
    };
    const next = existing
      ? gifts.map(g => (g.id === existing.id ? item : g))
      : [...gifts, item];
    try {
      await persistOrgJsonSetting(ORG_JSON_KEYS.freeResources, next, setOrg, 'freeResources');
      showToast(existing ? "Free resource updated" : "Free resource added", "success");
      closeForm();
    } catch {
      showToast("Could not save resource", "error");
    }
  };

  const attachFileToGift = async (giftId, file) => {
    if (!file) return;
    const safeName = `${Date.now()}_${file.name.replace(/[^\w.-]+/g, "_")}`;
    const blobPath = `org/free-resources/${safeName}`;
    setUploading(true);
    setAttachGiftId(giftId);
    try {
      await uploadClientDoc(blobPath, file);
      const downloadUrl = publicDocUrl(blobPath, file.name);
      const next = gifts.map(g => g.id === giftId
        ? {
          ...g,
          downloadUrl,
          storagePath: blobPath,
          fileName: file.name,
          format: file.name.split(".").pop()?.toUpperCase() || g.format,
        }
        : g);
      await persistOrgJsonSetting(ORG_JSON_KEYS.freeResources, next, setOrg, 'freeResources');
      showToast(`"${gifts.find(g => g.id === giftId)?.name || "Resource"}" file attached`, "success");
    } catch (e) {
      showToast(e.message || "Upload failed", "error");
    } finally {
      setUploading(false);
      setAttachGiftId(null);
    }
  };

  const removeGift = async (id) => {
    const next = gifts.filter(g => g.id !== id);
    await persistOrgJsonSetting(ORG_JSON_KEYS.freeResources, next, setOrg, 'freeResources');
    showToast("Resource removed", "success");
  };

  const toggleBundle = (bundleId) => {
    setDraft(d => ({
      ...d,
      bundleIds: d.bundleIds.includes(bundleId)
        ? d.bundleIds.filter(x => x !== bundleId)
        : [...d.bundleIds, bundleId],
    }));
  };

  return (
    <>
      {isAdmin && (
        <div style={{ marginBottom: 16, display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button className="btn btn-primary btn-sm" type="button" onClick={openAdd}>
            <InlineIcon name="add" size={14} gap={5}>Add free resource</InlineIcon>
          </button>
          <span style={{ fontSize: 12, color: "var(--muted)", alignSelf: "center" }}>
            Link resources to bundles in Bundles settings, or set unlock rules below.
          </span>
        </div>
      )}

      {!regComplete && user?.role === "client" && (
        <div style={{ padding: "16px 20px", background: "linear-gradient(135deg,var(--navy),#1E3A5F)", borderRadius: 12, marginBottom: 20, color: "#fff" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div>
              <div style={{ fontSize: 18, marginBottom: 3, display: "flex", alignItems: "center", gap: 6 }}>
                Complete registration to unlock gifts <Icon name="gift" size={16} />
              </div>
              <div style={{ fontSize: 12, opacity: .7 }}>{doneTasks} of {totalTasks} tasks completed</div>
            </div>
            <div style={{ fontSize: 32, color: "#C9A14A", fontWeight: 700 }}>{progress}%</div>
          </div>
          <div style={{ height: 8, background: "rgba(255,255,255,.15)", borderRadius: 8, overflow: "hidden" }}>
            <div style={{ height: "100%", width: progress + "%", background: "linear-gradient(90deg,#C9A14A,#F5D98A)", borderRadius: 8, transition: ".6s" }} />
          </div>
        </div>
      )}

      {regComplete && user?.role === "client" && (
        <div style={{ padding: "16px 20px", background: "linear-gradient(135deg,var(--green),#15803D)", borderRadius: 12, marginBottom: 20, color: "#fff", display: "flex", alignItems: "center", gap: 14 }}>
          <Icon name="sparkles" size={32} />
          <div>
            <div style={{ fontSize: 18 }}>Registration complete — gifts unlocked!</div>
            <div style={{ fontSize: 12, opacity: .8 }}>Download your free resources below.</div>
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 16 }}>
        {gifts.map(gift => {
          const open = isOpenForUser(gift);
          const fileReady = hasFile(gift);
          const linkedBundles = (gift.bundleIds || []).map(id => bundles?.find(b => b.id === id)?.name).filter(Boolean);
          return (
            <div key={gift.id} className="card" style={{ opacity: open ? 1 : .6, position: "relative", overflow: "hidden" }}>
              {!open && user?.role === "client" && (
                <div style={{ position: "absolute", inset: 0, background: "rgba(247,246,242,.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2, backdropFilter: "blur(2px)" }}>
                  <div style={{ textAlign: "center" }}>
                    <Icon name="lock" size={28} />
                    <div style={{ fontSize: 12, color: "var(--navy)", fontWeight: 700, marginTop: 6 }}>
                      {gift.triggerOn === "bundle_purchase" || linkedBundles.length
                        ? `Unlock with bundle: ${linkedBundles.join(", ") || "purchase"}`
                        : "Complete registration to unlock"}
                    </div>
                  </div>
                </div>
              )}
              <div className="card-body">
                {isStaff && (
                  <div style={{ float: "right", display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end", maxWidth: "70%" }}>
                    <button type="button" className="btn btn-sm" onClick={() => openEdit(gift)}>
                      <InlineIcon name="edit" size={12} gap={4}>Edit</InlineIcon>
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm"
                      title={gift.locked ? "Locked for clients — click to unlock" : "Unlocked for clients — click to lock"}
                      onClick={() => toggleGiftLock(gift.id)}
                      style={gift.locked ? { background: "#FEF2F2", color: "var(--red)" } : { background: "#F0FDF4", color: "var(--green)" }}
                    >
                      <InlineIcon name={gift.locked ? "lock" : "check"} size={12} gap={4}>
                        {gift.locked ? "Locked" : "Unlocked"}
                      </InlineIcon>
                    </button>
                    {isAdmin && (
                      <button type="button" className="btn btn-sm" style={{ color: "var(--red)", background: "#FEF2F2" }} onClick={() => removeGift(gift.id)}>Remove</button>
                    )}
                  </div>
                )}
                <div style={{ width: 48, height: 48, borderRadius: 12, background: `${gift.color || "var(--gold)"}18`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 12 }}>
                  <Icon name={gift.icon} size={24} color={gift.color || "var(--gold)"} />
                </div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "var(--navy)", marginBottom: 6 }}>{gift.name}</div>
                <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 4 }}>{gift.desc}</div>
                <div style={{ fontSize: 11, color: "var(--faint)", marginBottom: 14 }}>
                  {gift.format} · {gift.pages} pages
                  {linkedBundles.length ? ` · Bundles: ${linkedBundles.join(", ")}` : ""}
                  {isStaff && !fileReady && <span style={{ color: "var(--orange)", fontWeight: 600 }}> · No file attached</span>}
                  {isStaff && fileReady && <span style={{ color: "var(--green)", fontWeight: 600 }}> · File ready</span>}
                </div>
                {isStaff ? (
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    style={{ width: "100%", background: fileReady ? "var(--navy)" : "var(--gold-dk)" }}
                    disabled={uploading && attachGiftId === gift.id}
                    onClick={() => (fileReady ? download(gift) : promptAttach(gift.id))}
                  >
                    {uploading && attachGiftId === gift.id
                      ? "Uploading…"
                      : fileReady
                        ? <InlineIcon name="download" size={13} gap={5}>Download / Preview</InlineIcon>
                        : <InlineIcon name="upload" size={13} gap={5}>Attach file</InlineIcon>}
                  </button>
                ) : (
                  <button className="btn btn-primary btn-sm" style={{ width: "100%", background: open ? "var(--navy)" : "var(--muted)" }} disabled={!open} onClick={() => download(gift)}>
                    {open ? <InlineIcon name="download" size={13} gap={5}>Download Free</InlineIcon> : <InlineIcon name="lock" size={13} gap={5}>Locked</InlineIcon>}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <input
        ref={attachRef}
        type="file"
        hidden
        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip"
        onChange={e => {
          const file = e.target.files?.[0];
          const giftId = pendingAttachRef.current || attachGiftId;
          e.target.value = "";
          pendingAttachRef.current = null;
          if (file && giftId) attachFileToGift(giftId, file);
          else setAttachGiftId(null);
        }}
      />

      {showForm && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeForm()}>
          <div className="modal-box" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">{editId ? "Edit free resource" : "Add free resource"}</div>
              <button type="button" className="modal-close" onClick={closeForm}><Icon name="close" size={16} /></button>
            </div>
            <div className="modal-body">
              <GiftFormFields
                draft={draft}
                setDraft={setDraft}
                bundles={bundles}
                toggleBundle={toggleBundle}
                fileRef={fileRef}
                uploading={uploading}
                existingFileName={editingGift?.fileName}
              />
            </div>
            <div className="modal-foot">
              <button className="btn" type="button" onClick={closeForm}>Cancel</button>
              <button className="btn btn-primary" type="button" onClick={saveGift} disabled={uploading}>
                {uploading ? "Uploading…" : editId ? "Save changes" : "Save resource"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export { FreeGiftsPage };
